import { Box, CircularProgress, Grid, Stack, } from '@mui/material';
import AuthorSearch from './components/AuthorSearch';
import RiskScore from './components/RiskScore';
import LatestRetractions from './components/LatestRetractions';
import HistoricalDashboard from './components/HistoricalDashboard';
import { useAuth } from './contexts/authContext';
import PortfolioContent from './components/PortfolioContent';
import PortfolioPage from './PortfolioPage';
import SubscribeForm from './components/SubscribeForm';
import { useEffect } from 'react';
import ApiContent from './components/ApiContent';
import RetractionControlCenter from './components/RetractionControlCenter';
import InstitutionalPage from './InstitutionalPage';
import ReferenceWizard from './components/authoring-tools/ReferenceWizard';
import InstitutionUserManager from './components/InstitutionUserManager';
import CountryPage from './CountryPage';
import AdvancedAuthorSearch from './components/AdvancedAuthorSearch';
import PublisherPage from './PublisherPage';

interface ArgosDashboardPageProps {
  selectedValue: number,
  authorSearchRequested: string | null,
  setSelectedValue: (newValue: number) => void
}

function ArgosDashboardPage({ selectedValue, setSelectedValue, authorSearchRequested }: ArgosDashboardPageProps) {

  const { userLoggedIn, isDemoAccount,
    isInstitutionalAccount, isCountryWideAccount, isPublisherAccount,
    isJournalistAccount, isPaidIndividualCustomer, authContextLoaded, institutionalRoles } = useAuth()

  interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
  }


  const query = new URLSearchParams(window.location.search);

  useEffect(() => {
    let requestedTab = Number(query.get('tab'))
    if (requestedTab && !Number.isNaN(requestedTab)
      && selectedValue !== requestedTab) {
      setSelectedValue(requestedTab)
    }
  }, []);

  function CustomTabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
      <div
        role="tabpanel"
        hidden={value !== index}
        id={`simple-tabpanel-${index}`}
        aria-labelledby={`simple-tab-${index}`}
        {...other}
      >
        {value === index && <Box>{children}</Box>}
      </div>
    );
  }

  function isProAccount() {
    return authContextLoaded && (isCountryWideAccount || isInstitutionalAccount || isPublisherAccount)
  }

  return (
    <>
      {userLoggedIn &&
        <Grid container mt={2} sx={{ width: { sm: '100%', md: '80%' } }} columnSpacing={2} rowSpacing={2}>
          <Grid item sm={12} >
            <CustomTabPanel value={selectedValue} index={-1}>
              {!authContextLoaded && <CircularProgress />}
              {(authContextLoaded && isCountryWideAccount) && <CountryPage />}
              {(authContextLoaded && !isCountryWideAccount && isInstitutionalAccount) && <InstitutionalPage />}
              {(authContextLoaded && isPublisherAccount) && <PublisherPage />}
              {(authContextLoaded) && <RetractionControlCenter />}
            </CustomTabPanel>
          </Grid>
          <Grid item sm={12} >
            <CustomTabPanel value={selectedValue} index={0}>
              <LatestRetractions />
            </CustomTabPanel>
          </Grid>
          <Grid item sm={12} >
            <CustomTabPanel value={selectedValue} index={1}>
              {!authContextLoaded && <CircularProgress />}
              {isProAccount() &&
                <AdvancedAuthorSearch authorSearchRequested={authorSearchRequested} />
              }
              {authContextLoaded && !isProAccount() &&
                <AuthorSearch authorSearchRequested={authorSearchRequested} />
              }
            </CustomTabPanel>
          </Grid>
          <Grid item sm={12} >
            <CustomTabPanel value={selectedValue} index={2}>
              <RiskScore />
            </CustomTabPanel>
          </Grid>
          <Grid item sm={12} >
            <CustomTabPanel value={selectedValue} index={3}>
              <ReferenceWizard />
            </CustomTabPanel>
          </Grid>
          <Grid item sm={12} >
            <CustomTabPanel value={selectedValue} index={4}>
              {!isPaidIndividualCustomer && !isJournalistAccount && !isInstitutionalAccount
                && isDemoAccount
                &&
                <Stack
                  direction="column"
                  component="main"
                  sx={[
                    {
                      justifyContent: 'space-between',
                      height: { xs: 'auto', md: '100%' },
                    },
                    (theme) => ({
                      marginTop: -8,
                      backgroundImage:
                        'radial-gradient(ellipse at 70% 51%, hsl(210, 100%, 97%), hsl(0, 0%, 100%))',
                      backgroundSize: 'cover',
                      ...theme.applyStyles('dark', {
                        backgroundImage:
                          'radial-gradient(at 70% 51%, hsla(210, 100%, 16%, 0.5), hsl(220, 30%, 5%))',
                      }),
                    }),
                  ]}
                >
                  <Stack
                    direction={{ xs: 'column-reverse', md: 'row' }}
                    sx={{
                      justifyContent: 'center',
                      gap: { xs: 6, sm: 12 },
                      p: { xs: 2, sm: 4 },
                      m: 'auto',
                      mt: 0
                    }}
                  >
                    <PortfolioContent />
                    <SubscribeForm productPricePerMonth='10' productName='Argos Pro' productId='prod_RFqIk79lEN6z5I' addFreeNotice={true}
                      annualProductId='prod_RFqIk79lEN6z56' productPricePerAnnum='100'
                    />
                  </Stack>
                </Stack>
              }
              {(isPaidIndividualCustomer || isJournalistAccount ||
                (!isDemoAccount && (isPublisherAccount || isInstitutionalAccount))) &&
                <PortfolioPage />
              }
            </CustomTabPanel>
          </Grid>
          <Grid item sm={12}>
            <CustomTabPanel value={selectedValue} index={5}>
              <ApiContent />
            </CustomTabPanel>
          </Grid>
          <Grid>
            <CustomTabPanel value={selectedValue} index={6}>
              <HistoricalDashboard />
            </CustomTabPanel>
          </Grid>
          <Grid>
            <CustomTabPanel value={selectedValue} index={7}>
              {institutionalRoles.length > 0 &&
                <InstitutionUserManager shortInstitutionId={institutionalRoles[0][0].replace("https://openalex.org/", "")} />
              }
            </CustomTabPanel>
          </Grid>
        </Grid>
      }
    </>
  );
}

export default ArgosDashboardPage;
